import { useEffect } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { Lang } from '@/i18n/translations'

export const SITE_URL = 'https://eastudio.org'

/**
 * No react-helmet in package.json (checked before adding anything). Handles
 * title, meta description, canonical, hreflang alternates (bs/en/x-default),
 * and the Open Graph/Twitter title+description+url+locale tags that live in
 * index.html — all restored to their previous value on unmount so
 * navigating between routes/languages doesn't leak stale metadata.
 *
 * `path` is the LANG-NEUTRAL path after the `/bs`/`/en` segment — e.g. ''
 * for the homepage, '/blog', `/blog/${slug}`, '/kontakt', '/privacy'. Since
 * every route mirrors exactly between languages (same slugs, no localized
 * paths — see lib/blog.ts), the bs/en hreflang pair for any page is just
 * that same `path` under each lang prefix. Omit `path` (e.g. the 404 page)
 * to skip canonical/hreflang entirely and set `noindex` instead.
 */
export function useDocumentMeta({
  title,
  description,
  path,
  noindex = false,
}: {
  title: string
  description?: string
  path?: string
  noindex?: boolean
}) {
  const { lang } = useLanguage()

  useEffect(() => {
    const prevTitle = document.title
    document.title = title

    const restoreFns: Array<() => void> = []

    function patchMeta(selector: string, content: string) {
      const el = document.querySelector<HTMLMetaElement>(selector)
      if (!el) return
      const prev = el.getAttribute('content')
      el.setAttribute('content', content)
      restoreFns.push(() => {
        if (prev !== null) el.setAttribute('content', prev)
      })
    }

    function upsertLink(selector: string, tag: () => HTMLLinkElement, href: string) {
      let el = document.querySelector<HTMLLinkElement>(selector)
      let created = false
      const prevHref = el?.getAttribute('href') ?? null
      if (!el) {
        el = tag()
        document.head.appendChild(el)
        created = true
      }
      el.setAttribute('href', href)
      restoreFns.push(() => {
        if (created) el!.remove()
        else if (prevHref !== null) el!.setAttribute('href', prevHref)
      })
    }

    if (description) patchMeta('meta[name="description"]', description)
    patchMeta('meta[property="og:title"]', title)
    if (description) patchMeta('meta[property="og:description"]', description)
    if (description) patchMeta('meta[name="twitter:title"]', title)
    if (description) patchMeta('meta[name="twitter:description"]', description)
    patchMeta('meta[property="og:locale"]', lang === 'en' ? 'en_US' : 'bs_BA')

    if (path !== undefined) {
      const canonicalUrl = `${SITE_URL}/${lang}${path}`
      upsertLink('link[rel="canonical"]', () => {
        const l = document.createElement('link')
        l.setAttribute('rel', 'canonical')
        return l
      }, canonicalUrl)
      patchMeta('meta[property="og:url"]', canonicalUrl)

      const langs: Lang[] = ['bs', 'en']
      for (const l of langs) {
        upsertLink(
          `link[rel="alternate"][hreflang="${l}"]`,
          () => {
            const el = document.createElement('link')
            el.setAttribute('rel', 'alternate')
            el.setAttribute('hreflang', l)
            return el
          },
          `${SITE_URL}/${l}${path}`,
        )
      }
      // x-default points at the bs version — bs is the fallback the "/" redirect lands on.
      upsertLink(
        'link[rel="alternate"][hreflang="x-default"]',
        () => {
          const el = document.createElement('link')
          el.setAttribute('rel', 'alternate')
          el.setAttribute('hreflang', 'x-default')
          return el
        },
        `${SITE_URL}/bs${path}`,
      )
    }

    let robotsTag: HTMLMetaElement | null = null
    let createdRobots = false
    if (noindex) {
      robotsTag = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
      if (!robotsTag) {
        robotsTag = document.createElement('meta')
        robotsTag.setAttribute('name', 'robots')
        document.head.appendChild(robotsTag)
        createdRobots = true
      }
      robotsTag.setAttribute('content', 'noindex, nofollow')
    }

    return () => {
      document.title = prevTitle
      restoreFns.forEach((fn) => fn())
      if (robotsTag) {
        if (createdRobots) robotsTag.remove()
        else robotsTag.setAttribute('content', 'index, follow')
      }
    }
  }, [title, description, path, noindex, lang])
}
